import { APP_BASE_HREF, PlatformLocation } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter } from "@angular/router";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from "@azure/msal-angular";
import {
  BrowserCacheLocation,
  InteractionType,
  PublicClientApplication,
} from "@azure/msal-browser";
import { NgxDaterangepickerMd } from "ngx-daterangepicker-material";
import {
  authority,
  authorityDomain,
  clientId,
  getYMSScopes,
} from "./app/core/auth/auth.utils";
import { DateTimePipe } from "./app/core/pipes/date-time.pipe";
import { GlobalErrorLoggingService } from "./app/global-error-logging.service";
import { MsalReadyInterceptor } from "./app/msal-ready.interceptor";
import { NetworkResponseErrorInterceptor } from "./app/network-response-error.interceptor";
import { NetworkResponseTimeLoggingInterceptor } from "./app/network-response-time-logging-interceptor";
import { APP_ROUTES } from "./app.routes";
import { environment } from "./environments";
import { appendUrlPath } from "./utils";

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideRouter(APP_ROUTES),
    provideHttpClient(withInterceptorsFromDi()),

    importProvidersFrom(NgxDaterangepickerMd.forRoot()),
    DateTimePipe,

    {
      provide: APP_BASE_HREF,
      useFactory: (pl: PlatformLocation) => pl.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },

    {
      provide: ErrorHandler,
      useClass: GlobalErrorLoggingService,
    },

    {
      provide: MSAL_INSTANCE,
      useFactory(baseHref: string): PublicClientApplication {
        const baseUrl = appendUrlPath(window.location.origin, baseHref);
        return new PublicClientApplication({
          auth: {
            authority,
            clientId,
            knownAuthorities: [authorityDomain],
            navigateToLoginRequestUrl: true,
            redirectUri: appendUrlPath(baseUrl, "login").href,
          },
          cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
          },
        });
      },
      deps: [APP_BASE_HREF],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory(): MsalGuardConfiguration {
        return {
          authRequest: { prompt: "select_account" },
          interactionType: InteractionType.Redirect,
        };
      },
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory(): MsalInterceptorConfiguration {
        const protectedResourceMap = new Map<string, string[]>();
        protectedResourceMap.set(
          environment.apiUrls.yms.href,
          getYMSScopes(["read", "write", "user_impersonation"]),
        );
        return {
          interactionType: InteractionType.Redirect,
          protectedResourceMap,
        };
      },
    },
    MsalBroadcastService,
    MsalGuard,
    MsalService,

    // MsalReadyInterceptor must run BEFORE MsalInterceptor to ensure MSAL
    // has completed any authentication interactions before token acquisition
    { provide: HTTP_INTERCEPTORS, useClass: MsalReadyInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },

    // custom interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NetworkResponseErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NetworkResponseTimeLoggingInterceptor,
      multi: true,
    },
  ],
};
